import React, { useState } from 'react';
import { PinEntryRow } from './PinEntryRow';
import {
  PIN_PLACEHOLDER,
  PIN_SUBMIT_LABEL,
  PIN_SUBMITTING_LABEL,
  REQUEST_ACCESS_LABEL,
} from './spaceAccessCopy';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@components/ui/drawer';
import { COVERS_POST_SHEET } from '@lib/postSheet/coversPostSheet';

interface PinAccessDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitPin: (pin: string) => void;
  onRequestAccess: () => void;
  isSubmitting: boolean;
  error: string | null;
  /**
   * The line under the title; today's "Enter it to join this Space." when left out. A poll answer
   * that needs the PIN says what it waits on (TECH-197 E16).
   */
  description?: string;
}

export const PinAccessDrawer: React.FC<PinAccessDrawerProps> = ({
  open,
  onOpenChange,
  onSubmitPin,
  onRequestAccess,
  isSubmitting,
  error,
  description = 'Enter it to join this Space.',
}) => {
  // PinEntryRow owns the typed PIN; bumping its key on close clears it.
  const [rowKey, setRowKey] = useState(0);
  // Android back (grant.dismiss) flips `open` false without vaul calling
  // onOpenChange, so a reset tied to that handler would never run. Catch
  // every open→closed transition here via the render-phase previous-value
  // pattern instead.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) setRowKey(k => k + 1);
  }

  return (
    <Drawer open={open} repositionInputs={false} onOpenChange={onOpenChange}>
      {/* max-h (not h) preserves size-to-content; overflow keeps the submit
          button reachable when the numeric keyboard shrinks the viewport (#558,
          repositionInputs={false} above disables vaul's own keyboard avoidance). */}
      <DrawerContent className="max-h-[85dvh] overflow-y-auto" {...COVERS_POST_SHEET}>
        <DrawerHeader className="flex flex-col items-center gap-2.5 pt-5 pb-0">
          <div className="w-28 h-28 flex items-center justify-center">
            <img
              src="/assets/private-community.svg"
              alt=""
              className="w-full h-full"
              aria-hidden="true"
            />
          </div>

          {/* What the person can do here, not the lock: the drawer opens for any PIN Space, listed
              ones too, and the journey's waiting screen asks the same question (TECH-647). */}
          <DrawerTitle className="text-lg font-bold text-center">
            Got a PIN from the host?
          </DrawerTitle>

          <DrawerDescription className="text-sm text-gray-500 text-center leading-[18px] max-w-[310px]">
            {description}
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex flex-col gap-3 px-5 pb-safe-offset-5 pt-5">
          <PinEntryRow
            key={rowKey}
            label="PIN"
            placeholder={PIN_PLACEHOLDER}
            submitLabel={PIN_SUBMIT_LABEL}
            submittingLabel={PIN_SUBMITTING_LABEL}
            isSubmitting={isSubmitting}
            error={error}
            onSubmit={onSubmitPin}
          />

          {/* Request access link: ink (5.25:1 on white) and 44px tall (ui-ux-designer, Touch
              Targets). */}
          <button
            type="button"
            onClick={onRequestAccess}
            disabled={isSubmitting}
            className="min-h-11 rounded-full py-2.5 text-center text-sm font-medium text-mb-teal-ink hover:underline active:underline disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2"
          >
            {REQUEST_ACCESS_LABEL}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
